import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Flex,
  Typography,
  theme,
  DatePicker,
  InputNumber,
  message,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HttpRequest } from '../../../../../../utils/httpClient';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface FormIntegrationFunctionProps {
  functionData: {
    title: string;
    name: string;
    id: string;
    description: string;
    parameters: {
      properties: {
        [key: string]: {
          description: string;
          type: string;
          enum?: string[];
          form: boolean;
          pattern?: string;
          options?: { value: string; label: string }[];
          multiple?: boolean;
          value?: string | number | any;
        };
      };
      required: string[];
    };
    integration: {
      name: string;
      id: string;
      logo: string;
    };
    actionId?: string;
  };
  onBack: () => void;
  onSave: (message: string) => void;
  chatbotId?: string;
  flowId?: string;
}

interface FunctionInfo {
  name: string;
  parameters: any;
  authId: string | null;
  chatbotId: string | undefined;
  flowId: string | undefined;
  actionId?: string;
}

export const FormIntegrationFunction: React.FC<FormIntegrationFunctionProps> = ({
  functionData,
  onBack,
  onSave,
  chatbotId,
  flowId,
}) => {
  const { token } = useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [authId, setAuthId] = useState<string | null>(null);
  const initialValuesSet = useRef(false);

  // Log the functionData to help with debugging
  useEffect(() => {
    logger.info('FormIntegrationFunction - functionData:', functionData);
  }, [functionData]);

  // Cargar datos iniciales del formulario
  useEffect(() => {
    const initializeForm = async () => {
      // Si ya se inicializaron los valores, no hacer nada
      if (initialValuesSet.current) return;

      try {
        // Si hay actionId, cargar datos de la acción existente
        if (functionData.actionId) {
          logger.info('Loading existing action data for ID:', functionData.actionId);
          const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/flows/actions`);
          const response = await request.get(`${functionData.actionId}`);

          logger.info('Action data response:', response.data);

          if (response.data && response.data.action) {
            const { parameters, authId: actionAuthId } = response.data.action;

            logger.info('Setting form values with parameters from DB:', parameters);

            // Establecer el authId
            setAuthId(actionAuthId);

            // Establecer los valores del formulario
            if (parameters && Object.keys(parameters).length > 0) {
              form.setFieldsValue(parameters);
              initialValuesSet.current = true;
            }
          }
        }
        // Si no hay actionId, usar los valores de functionData.parameters
        else if (functionData.parameters && functionData.parameters.properties) {
          logger.info('Setting initial values from functionData');
          const initialValues = Object.entries(functionData.parameters.properties).reduce(
            (acc: any, [key, config]) => {
              if (config.value !== undefined) {
                acc[key] = config.value;
              }
              return acc;
            },
            {},
          );

          logger.info('Setting form values with parameters from props:', initialValues);
          form.setFieldsValue(initialValues);
          initialValuesSet.current = true;
        }
      } catch (error) {
        logger.error('Error initializing form data:', error);
        message.error('Failed to load action data');
      }
    };

    initializeForm();
  }, [functionData, form]);

  // Verificar si existe una autenticación para esta integración solo si no hay actionId
  useEffect(() => {
    const checkAuth = async () => {
      if (!chatbotId || !functionData.integration.id) return;

      try {
        // Obtener todas las autenticaciones para este chatbot
        const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/auth`);
        const response = await request.get(`${chatbotId}`);

        // Buscar la autenticación específica para esta integración
        const authForIntegration = response.data.auths?.find(
          (auth: any) => auth.integrationId === functionData.integration.id,
        );

        if (authForIntegration && authForIntegration.id) {
          // Solo establecer el authId si no se ha cargado de una acción existente
          if (!authId) {
            setAuthId(authForIntegration.id);
          }
        } else {
          message.warning(
            'No authentication found for this integration. Please authenticate first.',
          );
        }
      } catch (error) {
        logger.error('Error checking authentication:', error);
      }
    };

    if (!functionData.actionId && !authId) {
      checkAuth();
    }
  }, [chatbotId, functionData.integration.id, authId, functionData.actionId]);

  const handleSubmit = async (values: any) => {
    // Formatear los parámetros como un objeto JSON
    const paramsObject = Object.entries(values).reduce((acc: any, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    try {
      setLoading(true);

      if (!authId || !chatbotId) {
        throw new Error('Missing required authentication or chatbot information');
      }

      // Crear el mensaje incluyendo los parámetros y la información necesaria
      const functionInfo: FunctionInfo = {
        name: functionData.name,
        parameters: paramsObject,
        authId: authId,
        chatbotId: chatbotId,
        flowId: flowId,
      };

      // Si estamos editando una acción existente, incluir el actionId
      if (functionData.actionId) {
        functionInfo.actionId = functionData.actionId;
      }

      logger.info('Function info prepared:', functionInfo);

      const formattedMessage = `@${functionData.integration.id}/${functionData.id}${JSON.stringify(functionInfo)}`;

      message.success('Configuration prepared');
      onSave(formattedMessage);
      onBack();
    } catch (error) {
      logger.error('Error preparing configuration:', error);
      message.error('Failed to prepare configuration');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName: string, fieldConfig: any) => {
    if (!fieldConfig.form) {
      return null;
    }

    // Sanitizar el nombre del campo para el ID
    const sanitizedFieldName = fieldName.replace(/[^a-zA-Z0-9]/g, '_');

    if (Array.isArray(fieldConfig.enum)) {
      return (
        <Select
          placeholder={`Select ${fieldName}`}
          id={sanitizedFieldName}
          style={{ width: '100%' }}
        >
          {fieldConfig.enum.map((option: string) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
      );
    }

    if (Array.isArray(fieldConfig.options)) {
      return (
        <Select
          placeholder={`Select ${fieldName}`}
          id={sanitizedFieldName}
          style={{ width: '100%' }}
          mode={fieldConfig.multiple ? 'multiple' : undefined}
        >
          {fieldConfig.options.map((option: { value: string; label: string }) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    }

    switch (fieldConfig.type.toLowerCase()) {
      case 'select':
        return (
          <Select
            placeholder={fieldConfig.description}
            id={sanitizedFieldName}
            style={{ width: '100%' }}
          >
            {(fieldConfig.enum || []).map((option: string) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        );

      case 'string':
        if (
          fieldConfig.description?.toLowerCase().includes('description') ||
          fieldName.toLowerCase().includes('description')
        ) {
          return <TextArea placeholder={fieldConfig.description} id={sanitizedFieldName} />;
        }
        return (
          <Input
            placeholder={fieldConfig.description}
            pattern={fieldConfig.pattern}
            id={sanitizedFieldName}
          />
        );

      case 'number':
      case 'integer':
        return (
          <InputNumber
            style={{ width: '100%' }}
            placeholder={fieldConfig.description}
            id={sanitizedFieldName}
          />
        );

      case 'date':
        return <DatePicker style={{ width: '100%' }} id={sanitizedFieldName} />;

      case 'daterange':
        return <RangePicker style={{ width: '100%' }} id={sanitizedFieldName} />;

      default:
        logger.warn(`Unknown field type: ${fieldConfig.type} for field ${fieldName}`);
        return <Input placeholder={fieldConfig.description} id={sanitizedFieldName} />;
    }
  };

  return (
    <Card style={{ maxWidth: 650, margin: '0 auto', border: 'none' }}>
      <Flex vertical gap={token.marginMD}>
        <Flex align="center" gap={token.marginSM}>
          <Flex vertical align="center" style={{ flex: 1 }}>
            <img
              src={functionData.integration.logo}
              alt={`${functionData.integration.name} Logo`}
              style={{
                height: 80,
                width: 80,
                objectFit: 'contain',
                marginBottom: token.marginXS,
              }}
            />
            <Title level={4} style={{ margin: 0 }}>
              {functionData.integration.name}
            </Title>
          </Flex>
        </Flex>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {Object.entries(functionData.parameters.properties)
            .filter(([_, fieldConfig]) => fieldConfig.form)
            .map(([fieldName, fieldConfig]) => {
              // Sanitizar el nombre del campo para el ID
              const sanitizedFieldName = fieldName.replace(/[^a-zA-Z0-9]/g, '_');

              return (
                <Form.Item
                  key={fieldName}
                  name={fieldName}
                  label={fieldConfig.description || fieldName}
                  rules={[
                    {
                      required: functionData.parameters.required.includes(fieldName),
                      message: `Please input ${fieldConfig.description || fieldName}!`,
                    },
                    ...(fieldConfig.pattern
                      ? [
                          {
                            pattern: new RegExp(fieldConfig.pattern),
                            message: `Invalid format for ${fieldConfig.description || fieldName}`,
                          },
                        ]
                      : []),
                  ]}
                >
                  {renderField(fieldName, fieldConfig)}
                </Form.Item>
              );
            })}

          <Flex gap={token.marginSM} justify="flex-end">
            <Button onClick={onBack}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
              loading={loading}
              disabled={!authId || !chatbotId}
            >
              Save
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Card>
  );
};
